import { useIntervalFn, useDocumentVisibility } from "@vueuse/core";
import { h } from "vue";
import { Button } from "#components";

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const HIDDEN_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Polls api/identity/info every 5 minutes (visible tab) and immediately after
 * the tab becomes visible again following 5+ minutes of being hidden.
 * On a version mismatch between the baked-in client build and the server's
 * RELEASE_VERSION, shows a persistent warning toast and stops polling.
 *
 * No-op in local development (releaseVersion === "local").
 * Must be called client-side only.
 */
export default function useVersionCheck() {
    const config = useRuntimeConfig();

    // Allow a test-only version override via localStorage so E2E tests can exercise the
    // mismatch flow against a production build. The key is obscure enough that no real
    // user will set it, so checking unconditionally is safe.
    const testOverride = localStorage.getItem("_test_release_version");
    const clientVersion = testOverride ?? (config.public.releaseVersion as string);

    // Skip polling in local development or if the version is not set.
    if (!clientVersion || clientVersion === "local") return;

    const { $api } = useNuxtApp();
    const rollbar = useRollbar();
    const { toast } = useToast();
    const { gtag } = useGtag();
    const documentVisibility = useDocumentVisibility();
    const { t } = useI18n();

    let hiddenAt: number | null = null;
    let mismatchDetected = false;

    async function checkVersion() {
        if (mismatchDetected) return;

        try {
            const data = await $api<AccountData>("identity/info");

            if (data.ReleaseVersion && data.ReleaseVersion !== "local" && data.ReleaseVersion !== clientVersion) {
                mismatchDetected = true;
                pause();
                gtag("event", "version_mismatch", {
                    client_version: clientVersion,
                    server_version: data.ReleaseVersion,
                });
                showMismatchToast();
            }
        } catch (err) {
            rollbar?.error(err as Error);
        }
    }

    function showMismatchToast() {
        toast({
            title: t("Global.UpdateAvailable"),
            description: h("div", { class: "flex flex-col gap-2" }, [
                h("p", null, t("Global.NewVersionReleased")),
                h(Button, { size: "sm", variant: "accent", onClick: () => reloadNuxtApp() }, () => [
                    t("Global.RefreshNow"),
                ]),
            ]),
            variant: "warning",
            duration: Number.POSITIVE_INFINITY,
        });
    }

    const { pause, resume } = useIntervalFn(checkVersion, POLL_INTERVAL_MS, { immediate: false });

    watch(documentVisibility, (visibility) => {
        if (visibility === "hidden") {
            hiddenAt = Date.now();
            pause();
        } else if (visibility === "visible") {
            const hiddenDuration = hiddenAt !== null ? Date.now() - hiddenAt : 0;
            hiddenAt = null;

            // Re-check immediately, then let the interval take over.
            if (hiddenDuration >= HIDDEN_THRESHOLD_MS) checkVersion();

            if (!mismatchDetected) resume();
        }
    });

    // Respect the tab's current visibility at setup time.
    // If the tab is already hidden (e.g. opened in a background tab), don't start
    // polling yet — record when it was hidden so the watcher can correctly compute
    // the hidden duration when the tab eventually becomes visible.
    if (documentVisibility.value === "hidden") {
        hiddenAt = Date.now();
    } else {
        resume();
    }

    // In test mode, fire the first check immediately rather than waiting for the interval.
    if (testOverride) checkVersion();
}
